<!--
  @component GuardedActionModal — confirm dialog for effecting ops.
  tier:      primitive (visual atom)
  purpose:   the consent gate for every dispatch from the UI. The
             visual representative of the "exactly-once / FACE-
             LEDGER / no-fake-results" discipline at the seam.
-->
<script lang="ts">
  import type { Snippet } from "svelte";

  interface Props {
    open: boolean;
    title: string;
    /** Plain-text body. Pass `children` instead for rich body
     *  content (the implicit-children slot renders below the title
     *  and above the footer buttons). */
    body?: string;
    children?: Snippet;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Highlights the confirm button in error-red (e.g. for
     *  rollback, drop, teardown). */
    destructive?: boolean;
    /** Disables the confirm button + shows "…" while the parent's
     *  effecting op is in flight. */
    pending?: boolean;
    /** Disables the confirm button WITHOUT changing the label.
     *  Used for type-to-confirm gates and other parent-side
     *  validation that should block submit until satisfied. */
    disabled?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
  }
  const {
    open,
    title,
    body,
    children,
    confirmLabel = "confirm",
    cancelLabel = "cancel",
    destructive = false,
    pending = false,
    disabled = false,
    onConfirm,
    onCancel,
  }: Props = $props();

  function onKey(e: KeyboardEvent): void {
    if (!open) return;
    if (e.key === "Escape") onCancel();
    if (e.key === "Enter" && !pending && !disabled) onConfirm();
  }
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div class="overlay" role="dialog" aria-modal="true" aria-label={title}>
    <div class="modal">
      <header class="title">{title}</header>
      <div class="body">
        {#if children}
          {@render children()}
        {:else if body}
          <p>{body}</p>
        {/if}
      </div>
      <footer>
        <button type="button" class="cancel" onclick={onCancel}>
          {cancelLabel}
        </button>
        <button
          type="button"
          class="confirm"
          data-destructive={destructive}
          onclick={onConfirm}
          disabled={pending || disabled}
        >
          {pending ? "…" : confirmLabel}
        </button>
      </footer>
    </div>
  </div>
{/if}

<style>
  .overlay {
    position: fixed;
    inset: 0;
    background: var(--surface-overlay);
    backdrop-filter: var(--glass-blur);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: var(--space-4);
  }
  .modal {
    background: var(--surface-raised);
    border: 0.5px solid var(--border-strong);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    max-width: 480px;
    width: 100%;
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    font-family: var(--type-font);
  }
  .title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
  }
  .body {
    font-size: 13px;
    line-height: 1.5;
    color: var(--text-secondary);
  }
  .body :global(p) {
    margin: 0;
  }
  footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-2);
  }
  button {
    appearance: none;
    font: inherit;
    font-size: 12px;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    cursor: pointer;
    border: 0.5px solid var(--border);
    background: var(--glass-fill);
    color: var(--text-primary);
    transition: background var(--ease-fast);
  }
  button:hover:not(:disabled) {
    background: var(--bg-hover);
  }
  button.confirm {
    background: var(--accent-subtle);
    color: var(--accent-text);
    border-color: var(--accent-border);
  }
  button.confirm[data-destructive="true"] {
    background: var(--error-subtle);
    color: var(--error);
    border-color: var(--error-subtle);
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
